import {
    ModalDelete,
    Pagination,
    RenderEmptyList,
    Spinner,
    SpinnerLoader,
    TooltipCustom,
    WithRoleAccess,
    WithModulesAccess,
} from "@services/ui-components";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import Form from "react-bootstrap/Form";
import { Button } from "react-bootstrap";
import { ApolloError, useReactiveVar } from "@apollo/client";
import { ErrorModalComponent } from "@me-pos/error-modal";
import i18next from "i18next";
import ErrorService from "../../../services/ErrorService";
import SocketConnection from "../../../services/SocketConnection";
import { debounce } from "@me-team/host/src/utils/utils";
import ToastComponent from "../../ui-components/ToastComponent/ToastComponent";
import { useToast } from "../../../hooks/useToast";
import { AllModulesTagName, ImportEntityType } from "../../../utils/enums";
import ServiceNameWithOptionalLink from "./ServiceNameWithOptionalLink";
import {
    useGetAllServicesQuery,
    useGetOneServiceQuery,
    useUpdateEmployeeServiceRelationsFromServiceMutation,
    useUpdateServiceMutation,
} from "@me-team/host/main/services/graphql/services.hooks";
import { CompanyAccess, ServiceInput, ServiceProxy } from "@me-team/host/main/graphql/types";
import JoyrideWrapper from "../../Joyride/JoyrideWrapper";
import { changeStepStorage } from "../../Joyride/joyrideCallbackHandler";
import { currencySingVar, currentBranchEmployeeIdVar, currentCompanyIdVar } from "@me-team/host/main/globalVar/state";
import { useRoleAccess } from "@me-team/host/src/utils/useRoleAccess";
import { useService } from "@me-team/common/src/hooks/useService";
import useScrollToTopOnChange from "../../../hooks/useScrollToTopOnChange";
import { isKeepOldValue } from "../ServiceProviderSettings/utils/helpers";
import ServiceProviderSettingsModal, {
    ServiceProviderUpdateOption,
} from "../ServiceProviderSettings/ServiceProviderSettingsModal/ServiceProviderSettingsModal";
import { ServiceChanges } from "../ServiceProviderSettings/utils/hooks/useServiceChangesTracker";
import { ServiceProviderSettingsNavigationState } from "../../../interfaces/interfaces";

export type filterType = {
    name: string;
    categoryId: number;
    subCategoryId: number;
    id?: number;
};

export type TableServicesPropsType = {
    filter: filterType;
};

interface PendingProviderSettings {
    serviceId: number;
    serviceName: string;
    changes: ServiceChanges;
}

const TableServices: React.FC<TableServicesPropsType> = ({ filter }) => {
    const { t } = useTranslation();
    const { branchId } = useParams();
    const navigate = useNavigate();
    const currentCompanyId = useReactiveVar(currentCompanyIdVar);
    const currentBranchEmployeeId = useReactiveVar(currentBranchEmployeeIdVar);
    const canCreateService = useRoleAccess(CompanyAccess.ServiceCreate);
    const canEditServiceAndOnlineBooking = useRoleAccess(CompanyAccess.ServiceAndOnlineBookingEdit);
    const currencySing = useReactiveVar(currencySingVar);
    const location = useLocation();
    const hasHandledNavigationStateRef = useRef(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(20);
    const [onlineRegistrations, setOnlineRegistrations] = useState<{ [key: number]: boolean }>({});
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
    const [showCannotDelete, setShowCannotDelete] = useState(false);
    const [deleteServiceId, setDeleteServiceId] = useState(null);
    const [isLoadingRefetchServices, setIsLoadingRefetchServices] = useState<boolean>(false);
    const [isErrorModalOpen, setIsErrorModalOpen] = useState<number>(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const runJoy = localStorage.getItem("run") || false;
    const step = localStorage.getItem("step") || "0";
    const [run] = useState(runJoy);
    const { showToast, toastText, toggleShowToast, setShowToast } = useToast();
    const [showProviderSettingsModal, setShowProviderSettingsModal] = useState(false);
    const [pendingProviderSettings, setPendingProviderSettings] = useState<PendingProviderSettings | null>(null);
    const [isSwitching, setIsSwitching] = useState<{ [key: number]: boolean }>({});

    useScrollToTopOnChange(currentPage);

    const [updateEmployeeServiceRelations] = useUpdateEmployeeServiceRelationsFromServiceMutation();

    const categoryId = filter?.subCategoryId ? filter?.subCategoryId : filter?.categoryId;

    useEffect(() => {
        const navigationState: ServiceProviderSettingsNavigationState | null = location.state;
        if (!navigationState) {
            hasHandledNavigationStateRef.current = false;
            return;
        }
        if (hasHandledNavigationStateRef.current) return;

        hasHandledNavigationStateRef.current = true;

        if (navigationState.toast && navigationState.text) {
            toggleShowToast(navigationState.text);
        }

        if (navigationState.openServiceProviderSettingsModal && navigationState.serviceProviderSettingsPayload) {
            setPendingProviderSettings(navigationState.serviceProviderSettingsPayload);
            setShowProviderSettingsModal(true);
        }

        // We clear the state so that the modal doesn't reopen when refreshing/backing
        navigate(location.pathname, { replace: true, state: null });
    }, [location.state, location.pathname, navigate, toggleShowToast]);

    const {
        data: servicesData,
        loading,
        refetch: refetchServicesData,
    } = useGetAllServicesQuery({
        variables: {
            page: currentPage,
            itemsPerPage: itemsPerPage,
            categoryId: categoryId,
            name: filter?.name,
            id: filter?.id,
            companyId: +currentCompanyId,
            employeeRelationsDeleted: false,
            connected: true,
            branchId: +branchId,
        },
        skip: !currentCompanyId,
        context: {
            errorType: "global",
        },
        onError: (error: ApolloError) => {
            setIsErrorModalOpen(ErrorService.errorHandling(error));
        },
    });

    useEffect(() => {
        refetchServicesData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const debouncedRefetch = useCallback(
        debounce(() => {
            setIsLoadingRefetchServices(true);
            refetchServicesData().then(() => {
                setIsLoadingRefetchServices(false);
            });
        }, 1000),
        [refetchServicesData]
    );

    useEffect(() => {
        if (currentCompanyId) {
            new SocketConnection(`${process.env.REACT_APP_SOCKET}/?companyId=${currentCompanyId}`, (data: any) => {
                if (data.entity === ImportEntityType.SERVICE_TYPE) {
                    debouncedRefetch();
                }
            });
        }
    }, [currentCompanyId, debouncedRefetch]);

    const {
        data: oneServiceData,
        loading: oneServiceDataLoading,
        refetch: refetchOneService,
    } = useGetOneServiceQuery({
        variables: {
            id: deleteServiceId && deleteServiceId,
            companyId: +currentCompanyId,
            branchId: Number(branchId),
        },
        skip: !currentCompanyId || !deleteServiceId,
        context: {
            errorType: "global",
        },
        onError: (error: ApolloError) => {
            setIsErrorModalOpen(ErrorService.errorHandling(error));
        },
    });

    const oneService = oneServiceData?.user?.company?.services?.services[0];

    const { handleDelete } = useService({
        service: oneService,
        onCompleted: (data) => {
            data && setShowDeleteConfirmation(false);
            data && refetchServicesData();
            toggleShowToast(t("Deleted"));
            setIsLoading(false);
        },
        onError: (error: ApolloError) => {
            setIsErrorModalOpen(ErrorService.errorHandling(error));
            setIsLoading(false);
        },
    });

    const [updateService] = useUpdateServiceMutation();
    useEffect(() => {
        setCurrentPage(1);
    }, [filter]);

    useEffect(() => {
        if (servicesData) {
            const initialRegistrations: { [key: number]: boolean } = {};
            servicesData?.user?.company?.services?.services?.forEach((service) => {
                initialRegistrations[service?.id] = service?.onlineRegistration;
            });
            setOnlineRegistrations(initialRegistrations);
        }
    }, [servicesData]);
    useEffect(() => {
        const element = document.querySelector("#table"); // Замените селектор на нужный

        if (element && window?.innerWidth <= 930) {
            element.scrollLeft = element.scrollWidth - element.clientWidth;
        }
    }, [loading, oneServiceDataLoading, isLoadingRefetchServices]);
    const handleConfirmationClose = () => setShowDeleteConfirmation(false);
    const handleCannotDeleteClose = () => setShowCannotDelete(false);

    const handleShowModalDelete = async (id: number) => {
        setDeleteServiceId(id);
        const { data } = await refetchOneService({
            id: id,
            employeeRelationsDeleted: false,
        });
        const getEmployee = data?.user?.company?.services?.services[0];
        if (getEmployee?.employeeServiceRelations?.length < 1) {
            setShowDeleteConfirmation(true);
        } else {
            setShowCannotDelete(true);
        }
    };

    const toggleOnlineRegistration = async (serviceId: number) => {
        if (isSwitching[serviceId]) return;
        setIsSwitching((prev) => ({ ...prev, [serviceId]: true }));
        const oldValue = Boolean(onlineRegistrations[serviceId]);
        const newValue = !oldValue;

        const rollback = () => {
            setOnlineRegistrations((prev) => ({ ...prev, [serviceId]: oldValue }));
            setIsSwitching((prev) => ({ ...prev, [serviceId]: false }));
        };

        setOnlineRegistrations((prev) => ({ ...prev, [serviceId]: newValue }));

        const { data } = await refetchOneService({ id: serviceId });
        const serviceData = data?.user?.company?.services?.services[0];

        if (!serviceData) {
            rollback();
            return;
        }

        const input: ServiceInput = {
            name: serviceData?.name,
            company: serviceData?.company?.id,
            category: serviceData?.serviceCategoryNew?.id,
            duration: serviceData?.duration,
            price: serviceData?.price,
            description: serviceData?.description,
            onlineBooking: newValue,
        };

        await updateService({
            variables: { id: serviceId, input, companyId: +currentCompanyId, branchId: +branchId },
            context: { errorType: "local" },
            onCompleted: () => {
                const performersCount = serviceData?.employeeServiceRelations?.length ?? 0;

                // If there are no performers, the ServiceProviderSettingsModal is not needed
                if (performersCount < 1) {
                    toggleShowToast(t("Saved"));
                    setIsSwitching((prev) => ({ ...prev, [serviceId]: false }));
                    return;
                }
                setPendingProviderSettings({
                    serviceId,
                    serviceName: serviceData?.name ?? "",
                    changes: {
                        online: { oldValue, newValue },
                    },
                });
                setShowProviderSettingsModal(true);

                setIsSwitching((prev) => ({ ...prev, [serviceId]: false }));
            },
            onError: (error: ApolloError) => {
                rollback();
                setIsErrorModalOpen(ErrorService.errorHandling(error));
            },
        });
    };

    const services = servicesData?.user?.company?.services?.services;
    const pageCount = parseInt(servicesData?.user?.company?.services?.pageCount || "0");
    const totalItems = servicesData?.user?.company?.services?.totalCount || 0;
    const firstItemIndex = (currentPage - 1) * itemsPerPage + 1;
    const lastItemIndex = Math.min(currentPage * itemsPerPage, totalItems);
    const isFilterNotEmpty = Object.values(filter).some((value) => value != null && value !== "");

    const sortedServices =
        services &&
        [...services].sort((a, b) => {
            if (a.deleted && !b.deleted) return 1;
            if (!a.deleted && b.deleted) return -1;
            return 0;
        });

    const handleEditService = (id: number) => {
        navigate(`/services/service/${currentCompanyId}/service-list/${id}/edit/${branchId}`);
    };

    const handleRecoveryService = (id: number) => {
        navigate(`/services/service/${currentCompanyId}/service-list/${id}/restore/${branchId}`);
    };

    const handlePageChange = (pageNumber: number) => {
        setCurrentPage(pageNumber);
    };
    const handleNavigatteToServiceProviders = (id: number) => {
        run && changeStepStorage("next");
        navigate(`/services-providers/${id}/${branchId}`);
    };

    const handleProviderSettingsApply = useCallback(
        async (selections: {
            duration?: { option: ServiceProviderUpdateOption };
            price?: { option: ServiceProviderUpdateOption };
            online?: { option: ServiceProviderUpdateOption };
        }) => {
            if (!pendingProviderSettings || !currentCompanyId || !branchId) {
                setShowProviderSettingsModal(false);
                setPendingProviderSettings(null);
                return;
            }

            const { serviceId, changes } = pendingProviderSettings;

            try {
                const mutationPromises: Array<Promise<any>> = [];

                if (changes.duration && selections.duration && !isKeepOldValue(selections.duration.option)) {
                    mutationPromises.push(
                        updateEmployeeServiceRelations({
                            variables: {
                                companyId: Number(currentCompanyId),
                                serviceId,
                                branchId: Number(branchId),
                                employeeId: Number(currentBranchEmployeeId),
                                input: {
                                    updateOption: selections.duration.option,
                                    durationOldValue: changes.duration.oldValue,
                                },
                            },
                            context: { errorType: "local" },
                        })
                    );
                }

                if (changes.price && selections.price && !isKeepOldValue(selections.price.option)) {
                    mutationPromises.push(
                        updateEmployeeServiceRelations({
                            variables: {
                                companyId: Number(currentCompanyId),
                                serviceId,
                                branchId: Number(branchId),
                                employeeId: Number(currentBranchEmployeeId),
                                input: {
                                    updateOption: selections.price.option,
                                    priceOldValue: changes.price.oldValue,
                                },
                            },
                            context: { errorType: "local" },
                        })
                    );
                }

                if (changes.online && selections.online && !isKeepOldValue(selections.online.option)) {
                    mutationPromises.push(
                        updateEmployeeServiceRelations({
                            variables: {
                                companyId: Number(currentCompanyId),
                                serviceId,
                                branchId: Number(branchId),
                                employeeId: Number(currentBranchEmployeeId),
                                input: {
                                    updateOption: selections.online.option,
                                    onlineOldValue: changes.online.oldValue,
                                },
                            },
                            context: { errorType: "local" },
                        })
                    );
                }

                if (mutationPromises.length > 0) {
                    await Promise.all(mutationPromises);
                }

                toggleShowToast(t("Saved"));
            } catch (error) {
                setIsErrorModalOpen(ErrorService.errorHandling(error as ApolloError));
            } finally {
                setShowProviderSettingsModal(false);
                setPendingProviderSettings(null);
            }
        },
        [
            branchId,
            currentBranchEmployeeId,
            currentCompanyId,
            pendingProviderSettings,
            t,
            toggleShowToast,
            updateEmployeeServiceRelations,
        ]
    );

    const handleProviderSettingsCancel = useCallback(() => {
        setShowProviderSettingsModal(false);
        setPendingProviderSettings(null);
    }, []);

    if (loading || oneServiceDataLoading || (isLoadingRefetchServices && !showProviderSettingsModal))
        return <Spinner />;

    return (
        <>
            {step === "12" && <JoyrideWrapper run={run} />}
            <div
                id={"table"}
                className={
                    step === "12" && run && window?.innerWidth > 930
                        ? "col-12 table-responsive scrollbar"
                        : "col-12  overflow-auto"
                }
            >
                {!!services?.length ? (
                    <table className="table">
                        <thead>
                            <tr className="fs-7">
                                <th className="py-3 col-3 ps-4 align-middle">{t("service")}</th>
                                <WithModulesAccess modules={[AllModulesTagName.BOOKING]}>
                                    <th className="py-3 col-2 align-middle">{t("onlineRegistration")}</th>
                                </WithModulesAccess>
                                <th className="py-3 col-1 align-middle">{t("cost")}</th>
                                <th className="py-3 col-2 align-middle">{t("category")}</th>
                                <WithModulesAccess
                                    modules={[AllModulesTagName.SERVICE_PROVIDES, AllModulesTagName.PERSONNEL]}
                                >
                                    <th className="py-3 col-1 align-middle">{t("performers")}</th>
                                </WithModulesAccess>
                                <WithModulesAccess modules={[AllModulesTagName.WAREHOUSES]}>
                                    <th className="py-3 col-1 align-middle ">{t("techCards")}</th>
                                </WithModulesAccess>
                                <th className="col-2"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {sortedServices.map((service: ServiceProxy) => (
                                <tr key={service?.id} className="align-middle ">
                                    <td
                                        className={`align-middle ps-4 ${
                                            service?.deleted ? "text-secondary" : "text-primary"
                                        }`}
                                    >
                                        <ServiceNameWithOptionalLink
                                            service={service}
                                            currentCompanyId={+currentCompanyId}
                                        />
                                    </td>
                                    <WithModulesAccess modules={[AllModulesTagName.BOOKING]}>
                                        <td className={`align-middle ${service?.deleted ? "text-secondary" : ""}`}>
                                            <Form>
                                                <Form.Check
                                                    type="switch"
                                                    id={`servicesTable-onlineBooking-field-${service?.id}`}
                                                    label={
                                                        onlineRegistrations[service?.id] ? t("allowed") : t("Forbidden")
                                                    }
                                                    checked={!!onlineRegistrations[service?.id]}
                                                    disabled={
                                                        !canEditServiceAndOnlineBooking ||
                                                        service?.deleted ||
                                                        Boolean(isSwitching[service?.id])
                                                    }
                                                    onChange={() => toggleOnlineRegistration(service?.id)}
                                                />
                                            </Form>
                                        </td>
                                    </WithModulesAccess>
                                    <td className={`align-middle ${service?.deleted ? "text-secondary" : ""}`}>
                                        {service?.price} {currencySing}
                                    </td>
                                    <td className={`align-middle ${service?.deleted ? "text-secondary" : "text-dark"}`}>
                                        <span className="d-block">{service?.serviceCategoryNew?.name}</span>
                                        <span
                                            className={`d-block fs-7 ${
                                                service?.deleted ? "text-secondary" : "text-grey"
                                            }`}
                                        >
                                            {service?.serviceCategoryNew?.parentCategory?.name
                                                ? `(${service?.serviceCategoryNew?.parentCategory?.name})`
                                                : ""}
                                        </span>
                                    </td>
                                    <WithModulesAccess
                                        modules={[AllModulesTagName.SERVICE_PROVIDES, AllModulesTagName.PERSONNEL]}
                                    >
                                        <td
                                            className={`align-middle ${service?.deleted ? "text-secondary" : "text-primary"}`}
                                        >
                                            <WithRoleAccess
                                                permission={CompanyAccess.ServiceEmployeesView}
                                                fallback={service?.employeeServiceRelations?.length}
                                            >
                                                <Link
                                                    to={`/services-providers/${service?.id}/${branchId}`}
                                                    className={service?.deleted ? "text-secondary" : "text-primary"}
                                                    style={{ textDecoration: "none" }}
                                                >
                                                    {service?.employeeServiceRelations?.length}
                                                </Link>
                                            </WithRoleAccess>
                                        </td>
                                    </WithModulesAccess>
                                    <WithModulesAccess modules={[AllModulesTagName.WAREHOUSES]}>
                                        <td
                                            className={`align-middle ${service?.deleted ? "text-secondary" : "text-primary"}`}
                                        >
                                            <WithRoleAccess
                                                permission={CompanyAccess.ServiceTechMapsView}
                                                fallback={service?.technologicalMapServiceRelations?.length}
                                            >
                                                <Link
                                                    to={`/services/service/${currentCompanyId}/service-list/${service?.id}/technological/maps/branch/${branchId}`}
                                                    className={service?.deleted ? "text-secondary" : "text-primary"}
                                                    style={{ textDecoration: "none" }}
                                                >
                                                    {service?.technologicalMapServiceRelations?.length}
                                                </Link>
                                            </WithRoleAccess>
                                        </td>
                                    </WithModulesAccess>
                                    <td className={`align-middle text-end px-0 ${service?.deleted && "d-none"}`}>
                                        <div className="d-flex gap-2 justify-content-end align-items-center flex-nowrap">
                                            <WithRoleAccess permission={CompanyAccess.ServiceListView}>
                                                <TooltipCustom placement="bottom" text={t("Edit services")}>
                                                    <Button
                                                        className="btn-sm p-2 btn-square"
                                                        style={{ width: "39px" }}
                                                        variant="outline-secondary"
                                                        onClick={() => handleEditService(service?.id)}
                                                    >
                                                        <i className="bi bi-pencil text-dark"></i>
                                                    </Button>
                                                </TooltipCustom>
                                            </WithRoleAccess>
                                            <WithModulesAccess
                                                modules={[
                                                    AllModulesTagName.SERVICE_PROVIDES,
                                                    AllModulesTagName.PERSONNEL,
                                                ]}
                                            >
                                                <WithRoleAccess permission={CompanyAccess.ServiceEmployeesView}>
                                                    <TooltipCustom placement="bottom" text={t("Related employees")}>
                                                        <Button
                                                            variant="outline-secondary"
                                                            className={`btn-sm p-2  btn-square service-performer service-performer`}
                                                            style={{ width: "39px" }}
                                                            onClick={() =>
                                                                handleNavigatteToServiceProviders(service?.id)
                                                            }
                                                        >
                                                            <i className="bi bi-people text-dark"></i>
                                                        </Button>
                                                    </TooltipCustom>
                                                </WithRoleAccess>
                                            </WithModulesAccess>
                                            <WithModulesAccess modules={[AllModulesTagName.WAREHOUSES]}>
                                                <WithRoleAccess permission={CompanyAccess.ServiceTechMapsView}>
                                                    <TooltipCustom
                                                        placement="bottom"
                                                        text={t("Technological maps of the" + " service")}
                                                    >
                                                        <Button
                                                            className="btn-sm p-2 btn-square"
                                                            style={{ width: "39px" }}
                                                            variant="outline-secondary"
                                                            onClick={() =>
                                                                navigate(
                                                                    `/services/service/${currentCompanyId}/service-list/${service?.id}/technological/maps/branch/${branchId}`
                                                                )
                                                            }
                                                        >
                                                            <i className="bi bi-map text-dark"></i>
                                                        </Button>
                                                    </TooltipCustom>
                                                </WithRoleAccess>
                                            </WithModulesAccess>
                                            <WithRoleAccess permission={CompanyAccess.ServiceDeleteRestore}>
                                                <TooltipCustom placement="bottom" text={t("Remove a services")}>
                                                    <Button
                                                        className="btn-sm p-2 btn-square"
                                                        style={{ width: "39px" }}
                                                        variant="outline-secondary"
                                                        onClick={() => handleShowModalDelete(service?.id)}
                                                    >
                                                        <i className="bi bi-trash3 text-dark"></i>
                                                    </Button>
                                                </TooltipCustom>
                                            </WithRoleAccess>
                                        </div>
                                    </td>
                                    <td
                                        className={`align-middle text-end px-0 h-100 ${
                                            service?.deleted ? "" : "d-none"
                                        }`}
                                    >
                                        <div className="d-flex justify-content-end align-items-center flex-nowrap">
                                            <span className="fs-7 badge bg-additional-grey align-middle text-grey d-inline-block px-3  py-2 rounded">
                                                {t("Archive")}
                                            </span>
                                            <WithRoleAccess permission={CompanyAccess.ServiceDeleteRestore}>
                                                <TooltipCustom placement="bottom" text={t("Restore")}>
                                                    <Button
                                                        className="btn-square align-middle ms-2 p-2"
                                                        variant="outline-secondary"
                                                        style={{ width: "39px" }}
                                                        onClick={() => handleRecoveryService(service?.id)}
                                                    >
                                                        <i className="bi bi-arrow-counterclockwise text-dark"></i>
                                                    </Button>
                                                </TooltipCustom>
                                            </WithRoleAccess>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                ) : (
                    <RenderEmptyList
                        isFilterNotEmpty={isFilterNotEmpty}
                        emptyFilterTitle="No data found for the given values"
                        emptyListTitle="servicesNotYetCreated"
                        onClickButton={() => navigate(`/services/service/${currentCompanyId}/new/${branchId}`)}
                        isEmployeeRole={!canCreateService}
                        visibilityBtn={canCreateService}
                        buttonName={
                            <span>
                                <i className="bi bi-plus-lg fw-bold me-1 w-100"></i>
                                {t("addService")}
                            </span>
                        }
                    />
                )}
            </div>
            {!!services?.length && (
                <div className="d-flex justify-content-between align-items-center mt-4 mb-5">
                    <Pagination
                        t={t}
                        pages={Number(pageCount)}
                        currentPage={currentPage}
                        onPageChange={handlePageChange}
                        firstItemIndex={firstItemIndex}
                        lastItemIndex={lastItemIndex}
                        totalItems={totalItems}
                    />
                </div>
            )}
            <ModalDelete
                show={showDeleteConfirmation}
                handleClose={handleConfirmationClose}
                title={t("Deleting a service")}
                body={t("Are you sure you want to delete the selected service?")}
                buttons={[
                    {
                        text: t("Cancel"),
                        onClick: handleConfirmationClose,
                        variant: "btn btn-outline-violet border-primary text-primary fw-normal",
                        className: "d-flex justify-content-center align-items-center w-100",
                    },
                    {
                        text: isLoading ? (
                            <SpinnerLoader variant="light" loadingText={t("Deleting...")} />
                        ) : (
                            t("Delete")
                        ),
                        onClick: () => {
                            setIsLoading(true);
                            handleDelete();
                        },
                        variant: "primary",
                        className: "w-100",
                        disabled: isLoading,
                    },
                ]}
            />
            <ModalDelete
                show={showCannotDelete}
                handleClose={handleCannotDeleteClose}
                title={t("It is not possible to delete the service")}
                variantTitle="text-danger"
                body={t(
                    "Deletion is impossible. The service has associated employees in one of the branches. Clear the performers to complete the service deletion."
                )}
                buttons={[
                    {
                        text: t("servicesPage.deleteModal.okBtn"),
                        onClick: handleCannotDeleteClose,
                        variant: "danger",
                        className: "w-100",
                    },
                ]}
            />
            {isErrorModalOpen ? (
                <ErrorModalComponent
                    i18n={i18next}
                    onClose={() => {
                        setIsErrorModalOpen(null);
                    }}
                    isOpen={!!isErrorModalOpen}
                    currentError={isErrorModalOpen}
                />
            ) : null}
            {showProviderSettingsModal && pendingProviderSettings && (
                <ServiceProviderSettingsModal
                    show={showProviderSettingsModal}
                    onClose={handleProviderSettingsCancel}
                    onApply={handleProviderSettingsApply}
                    changes={pendingProviderSettings.changes}
                    serviceName={pendingProviderSettings.serviceName}
                />
            )}
            {showToast && <ToastComponent show={showToast} setShow={setShowToast} text={toastText} />}
        </>
    );
};

export default TableServices;
